import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useIsRenterOnly } from '../hooks/useEffectiveRoles';
import { useTenantPath } from '../hooks/useTenant';
import { useTenantBranding } from '../context/TenantBrandingContext';
import TenantBranding from './TenantBranding';
import UserAvatar from './UserAvatar';
import FeedbackModal from './FeedbackModal';
import useInquiryUnreadCountPolling from '../hooks/useInquiryUnreadCountPolling';

// Standard-Navbar-Konfiguration (Fallback wenn keine in der DB konfiguriert)
const DEFAULT_NAVBAR_CONFIG = {
  bg: 'primary',
  variant: 'dark',
  className: '',
  buttonVariant: 'outline-light',
  buttonTextClass: ''
};

/**
 * Sichtbarkeits-Typen für Navigations-Items:
 * - ANY             – für alle Rollen sichtbar
 * - LANDLORD_ONLY   – nur für Vermieter/Admins/Members (nicht für RENTER)
 * - RENTER_INCLUDED – auch für RENTER sichtbar
 */
const VISIBILITY = {
  ANY: 'ANY',
  LANDLORD_ONLY: 'LANDLORD_ONLY',
  RENTER_INCLUDED: 'RENTER_INCLUDED'
};

/**
 * Konfigurationsarray der Haupt-Navigationseinträge.
 * id: eindeutige HTML-ID, path: App-Pfad, label: UI-Text
 */
const NAV_ITEMS = [
  {
    id: 'nav-link-properties',
    path: '/properties',
    label: 'Immobilien',
    visibility: VISIBILITY.LANDLORD_ONLY
  },
  {
    id: 'nav-link-contracts',
    path: '/contracts',
    label: 'Mietverträge',
    visibility: VISIBILITY.RENTER_INCLUDED
  },
  {
    id: 'nav-link-utility-bills',
    path: '/utility-bills',
    label: 'Nebenkosten',
    visibility: VISIBILITY.RENTER_INCLUDED
  },
  {
    id: 'nav-link-bookings',
    path: '/payments',
    label: 'Buchungen',
    visibility: VISIBILITY.LANDLORD_ONLY
  },
  {
    id: 'nav-link-correspondence',
    path: '/correspondence',
    label: 'Korrespondenz',
    visibility: VISIBILITY.RENTER_INCLUDED
  },
  {
    id: 'nav-link-meter-management',
    path: '/meter-management',
    label: 'Zählermanagement',
    visibility: VISIBILITY.RENTER_INCLUDED
  },
  {
    id: 'nav-link-rent-out',
    path: '/rent-out',
    label: 'Vermieten',
    visibility: VISIBILITY.LANDLORD_ONLY,
    hasBadge: true
  }
];

const Navigation = () => {
  const { user, logout } = useAuth();
  const isRenterOnly = useIsRenterOnly();
  const navigate = useNavigate();
  const location = useLocation();
  const { buildPath } = useTenantPath();
  const { navbar, name: tenantName } = useTenantBranding();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const closeNavbar = () => setExpanded(false);

  // Globaler Zaehler fuer ungelesene Anfragen — Badge neben "Vermieten"
  // Hook muss immer aufgerufen werden (Rules of Hooks), Badge wird bedingt gerendert.
  // Fuer RENTER kein Polling (enabled=false) — RENTER sehen "Vermieten" nicht.
  const inquiryUnreadCount = useInquiryUnreadCountPolling(user, !isRenterOnly);

  // Navbar-Konfiguration aus API mit Defaults mergen
  const navbarConfig = useMemo(
    () => navbar ? { ...DEFAULT_NAVBAR_CONFIG, ...navbar } : DEFAULT_NAVBAR_CONFIG,
    [navbar]
  );

  /**
   * Filtert die Navigationsliste anhand der Benutzerrolle.
   * Für RENTER: nur RENTER_INCLUDED-Items anzeigen.
   * Für alle anderen: alle Items anzeigen.
   */
  const visibleNavItems = useMemo(
    () => NAV_ITEMS.filter(item => {
      if (isRenterOnly) {
        return item.visibility === VISIBILITY.RENTER_INCLUDED || item.visibility === VISIBILITY.ANY;
      }
      return true;
    }),
    [isRenterOnly]
  );

  const isActive = (path) => location.pathname.startsWith(buildPath(path));

  const handleLogout = () => {
    logout();
    navigate(buildPath('/login'));
  };

  if (!user) return null;

  return (
    <>
      <Navbar bg={navbarConfig.bg} variant={navbarConfig.variant} expand="lg" className={`mb-4 ${navbarConfig.className}`} as="nav" aria-label="Hauptnavigation" expanded={expanded} onToggle={setExpanded}>
        <Container>
          <Navbar.Brand
            as={Link}
            to={buildPath('/dashboard')}
            id="nav-brand"
            aria-label={tenantName ? `${tenantName} – Zum Dashboard` : 'Zum Dashboard'}
            aria-current={isActive('/dashboard') ? 'page' : undefined}
            onClick={closeNavbar}
          >
            <TenantBranding variant="navbar" />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" id="nav-toggle" label="Navigation umschalten" aria-expanded={expanded} />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="mx-auto">
              {visibleNavItems.map(item => (
                <Nav.Link
                  key={item.id}
                  as={Link}
                  to={buildPath(item.path)}
                  id={item.id}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  onClick={closeNavbar}
                >
                  {item.label}
                  {/* Badge für "Vermieten" — nur für Nicht-RENTER sichtbar */}
                  {item.hasBadge && !isRenterOnly && (
                    <>
                      {/* MINOR-2: Live-Region fuer Screenreader — kuendigt Badge-Aenderungen
                          an ohne dass Screenreader den visuellen Badge doppelt vorlesen.
                          aria-hidden="true" am Badge verhindert das doppelte Vorlesen. */}
                      <span
                        aria-live="polite"
                        aria-atomic="true"
                        className="visually-hidden"
                        id="nav-rent-out-unread-live"
                      >
                        {inquiryUnreadCount > 0
                          ? `${inquiryUnreadCount} ungelesene Anfrage${inquiryUnreadCount !== 1 ? 'n' : ''}`
                          : ''}
                      </span>
                      {inquiryUnreadCount > 0 && (
                        <Badge
                          bg="danger"
                          className="ms-2"
                          aria-hidden="true"
                          id="nav-rent-out-unread-badge"
                        >
                          {inquiryUnreadCount}
                        </Badge>
                      )}
                    </>
                  )}
                </Nav.Link>
              ))}
            </Nav>
            <UserAvatar
              user={user}
              onFeedbackClick={() => { closeNavbar(); setShowFeedbackModal(true); }}
              onAccountClick={() => { closeNavbar(); navigate(buildPath('/account')); }}
              onUsersClick={isRenterOnly ? undefined : () => { closeNavbar(); navigate(buildPath('/users')); }}
              onLandlordClick={isRenterOnly ? undefined : () => { closeNavbar(); navigate(buildPath('/landlord')); }}
              onLoansClick={isRenterOnly ? undefined : () => { closeNavbar(); navigate(buildPath('/loans')); }}
              onBankAccountsClick={isRenterOnly ? undefined : () => { closeNavbar(); navigate(buildPath('/bank-accounts')); }}
              onLogout={() => { closeNavbar(); handleLogout(); }}
              isRenterOnly={isRenterOnly}
            />
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <FeedbackModal
        show={showFeedbackModal}
        onHide={() => setShowFeedbackModal(false)}
      />
    </>
  );
};

export default Navigation;
